<template>
  <div class="payment-status-page">
    <div class="wrapper">
      <div class="inner-wrapper">
        <div
          class="payment-status-top-bar d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mx-3 mx-md-5 my-3 gap-2 gap-md-0"
        >
          <div class="d-flex align-items-center w-100 w-md-auto">
            <div class="flex-shrink-0" v-if="insurerLogoSrc">
              <img :src="insurerLogoSrc" alt="" class="payment-status-insurer-logo" />
            </div>
            <div class="ms-3 ms-md-4" v-if="productName">
              <h4 class="mb-0">{{ productName }}</h4>
            </div>
          </div>
          <div class="propno w-100 w-md-auto">
            <JourneyIdContainer
              v-if="journeyId"
              :journey-id="String(journeyId)"
              label="JOURNEY ID :"
            />
          </div>
        </div>
        <div class="payment-status-content text-center">
          <HourglassIcon v-if="!showError" class="payment-status-hourglass" :size="96" />
          <h3
            class="payment-status-title"
            :class="{ 'payment-status-title--error': showError }"
          >{{ pageTitle }}</h3>
          <h6
            class="base-reg payment-status-subtitle"
            :class="{ 'payment-status-subtitle--error': showError }"
            v-html="pageSubtitle"
          ></h6>
          <template v-if="!showError">
            <p class="payment-status-countdown" aria-live="polite" v-if="showCountdown">{{ countdownLabel }}</p>
            <div
              class="progress payment-status-progress mx-auto"
              role="progressbar"
              :aria-valuenow="progressPercent"
              aria-valuemin="0"
              aria-valuemax="100"
            >
              <div class="progress-bar" :style="{ width: progressPercent + '%' }"></div>
            </div>
            <p class="payment-status-hint mt-3" v-if="statusHint">{{ statusHint }}</p>
          </template>
          <div v-else class="payment-status-error-actions mt-4">
            <button type="button" class="btn btn-primary" @click="goToDiy">
              {{ diyButtonLabel }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <PremiumDifferenceDialog
      v-model:visible="showPremiumDifferenceModal"
      :quotedPremium="quotedPremium"
      :receivedPremium="receivedPremium"
      @handleYes="handlePremiumDifferenceYes"
      @handleNo="handlePremiumDifferenceNo"
    />
  </div>
</template>

<script>
import HourglassIcon from '@/components/icons/HourglassIcon.vue';
import JourneyIdContainer from '@/components/common/JourneyIdContainer.vue';
import PremiumDifferenceDialog from '@/components/common/PremiumDifferenceDialog.vue';
import { stripHtml } from '@/utils/inputSanitizer';
import { shouldSkipGodigitKycOvd } from '@/utils/godigitKycOvdSkip';
import { navigateToPaymentStatusOutcome } from '@/lib/payment/navigate-payment-outcome';
import {
  DIY_CHECK_PATH,
  DIY_INLINE_ERROR_BUTTON_LABEL,
  DIY_INLINE_ERROR_TITLE,
  DIY_PROPOSAL_ERROR_STORAGE_KEY,
  PAYMENT_DETAILS_POLL_INTERVAL_MS,
  PAYMENT_DETAILS_POLL_TIMEOUT_MS,
  PAYMENT_GATEWAY_BACK_MESSAGE,
  PAYMENT_GATEWAY_FALLBACK_MESSAGE,
  PAYMENT_GATEWAY_REDIRECTED_STORAGE_KEY,
  PAYMENT_REDIRECT_PAUSE_MS,
  PAYMENT_STATUS_OUTCOME_INSPECTION,
  PAYMENT_URL_STAGE,
  PAYMENT_URL_STORAGE_KEY,
  PROPOSAL_SUBMITTING_HINT,
  PROPOSAL_SUBMITTING_SUBTITLE,
  PROPOSAL_SUBMITTING_TITLE,
  buildPaymentStatusRedirectUrl,
} from '@/lib/payment/payment-status-constants';

const FETCHING_PAYMENT_HINT = 'Fetching your payment link…';
const POST_PAYMENT_REDIRECT_HINT = 'You will be taken to the payment page in a moment.';
const VERIFYING_STATUS_TITLE = 'Verifying the status. Please wait…';
const VERIFYING_STATUS_SUBTITLE = 'Please do not refresh or close this window.';
const VERIFYING_STATUS_HINT = 'Verifying the status…';
const GATEWAY_REDIRECT_TITLE = 'Redirecting to Payment Gateway';
const GATEWAY_REDIRECT_SUBTITLE = 'Please wait. Do not refresh or close this window.';
const GATEWAY_REDIRECT_HINT = 'You will be taken to the payment page in a moment.';

export default {
  name: 'ProposalSubmissionPage',
  components: { HourglassIcon, JourneyIdContainer, PremiumDifferenceDialog },
  data() {
    return {
      progressPercent: 0,
      remainingMs: PAYMENT_DETAILS_POLL_TIMEOUT_MS,
      progressTimer: null,
      pollDeadline: 0,
      pageActive: true,
      statusHint: VERIFYING_STATUS_HINT,
      journeyId: '',
      configData: null,
      selectedPlan: null,
      selectedPremiumPlan: null,
      showPremiumDifferenceModal: false,
      quotedPremium: 0,
      receivedPremium: 0,
      ignorePremiumDifference: false,
      premiumDifferenceResponse: null,
      premiumChoiceHandled: false,
      postPaymentFlow: false,
      submissionStage: 'submitting',
      showError: false,
      errorMessage: '',
      diyButtonLabel: DIY_INLINE_ERROR_BUTTON_LABEL,
      onPageShowHandler: null,
    };
  },
  async mounted() {
    this.restoreSessionFromStorage();
    this.bindGatewayBackRecovery();

    if (this.consumeGatewayRedirectedFlag()) {
      this.showDiyError(PAYMENT_GATEWAY_BACK_MESSAGE);
      return;
    }

    this.startProgressBar();
    if (!this.journeyId) {
      this.showDiyError(PAYMENT_GATEWAY_FALLBACK_MESSAGE);
      return;
    }
    await this.loadFlowMode();
    if (this.shouldSkipSubmit()) {
      this.enterSubmittingStage();
      await this.paymentUrlPoll();
      return;
    }
    this.enterSubmittingStage();
    await this.proposalSubmission();
  },
  beforeUnmount() {
    this.pageActive = false;
    this.stopProgressBar();
    this.unbindGatewayBackRecovery();
  },
  computed: {
    countdownLabel() {
      const totalSeconds = Math.max(0, Math.ceil(this.remainingMs / 1000));
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      const paddedSeconds = String(seconds).padStart(2, '0');
      return `${minutes}:${paddedSeconds}`;
    },
    planForDisplay() {
      const plan = this.selectedPlan || this.selectedPremiumPlan;
      if (Array.isArray(plan)) {
        return plan[0] || null;
      }
      return plan;
    },
    productName() {
      return (
        this.planForDisplay?.insuranceAndProducts?.productName
        || this.planForDisplay?.insuranceAndProducts?.policyTypeDesc
        || ''
      );
    },
    insurerLogoSrc() {
      return this.planForDisplay?.supportingData?.insCompanyLogo || '';
    },
    showCountdown() {
      return !this.showError && this.submissionStage !== 'redirecting';
    },
    pageTitle() {
      if (this.showError) {
        return DIY_INLINE_ERROR_TITLE;
      }
      if (this.submissionStage === 'redirecting') {
        return this.postPaymentFlow ? 'Redirecting you to payment' : GATEWAY_REDIRECT_TITLE;
      }
      if (!this.postPaymentFlow) {
        return VERIFYING_STATUS_TITLE;
      }
      if (this.submissionStage === 'fetching') {
        return 'Proposal submitted... Please wait while we prepare your payment page';
      }
      return PROPOSAL_SUBMITTING_TITLE;
    },
    pageSubtitle() {
      if (this.showError) {
        return this.errorMessage || PAYMENT_GATEWAY_FALLBACK_MESSAGE;
      }
      if (this.submissionStage === 'redirecting') {
        return this.postPaymentFlow
          ? 'Please wait. Do not refresh or close this window.'
          : GATEWAY_REDIRECT_SUBTITLE;
      }
      if (!this.postPaymentFlow) {
        return VERIFYING_STATUS_SUBTITLE;
      }
      if (this.submissionStage === 'fetching') {
        return 'Please do not refresh or close this window. We are preparing your payment page.';
      }
      return PROPOSAL_SUBMITTING_SUBTITLE;
    },
  },
  methods: {
    bindGatewayBackRecovery() {
      this.onPageShowHandler = (event) => {
        if (!event?.persisted) {
          return;
        }
        if (!this.consumeGatewayRedirectedFlag()) {
          return;
        }
        this.showDiyError(PAYMENT_GATEWAY_BACK_MESSAGE);
      };
      window.addEventListener('pageshow', this.onPageShowHandler);
    },
    unbindGatewayBackRecovery() {
      if (this.onPageShowHandler) {
        window.removeEventListener('pageshow', this.onPageShowHandler);
        this.onPageShowHandler = null;
      }
    },
    consumeGatewayRedirectedFlag() {
      const flagged = sessionStorage.getItem(PAYMENT_GATEWAY_REDIRECTED_STORAGE_KEY);
      if (!['1', 'true'].includes(String(flagged))) {
        return false;
      }
      sessionStorage.removeItem(PAYMENT_GATEWAY_REDIRECTED_STORAGE_KEY);
      return true;
    },
    markGatewayRedirected() {
      sessionStorage.setItem(PAYMENT_GATEWAY_REDIRECTED_STORAGE_KEY, 'true');
    },
    shouldSkipSubmit() {
      return ['1', 'true'].includes(String(this.$route.query.skipSubmit || ''));
    },
    async loadFlowMode() {
      try {
        const response = await this.backendApi('getProposalStatus', [this.journeyId], {}, 'get');
        if (!this.pageActive || !response || response === 'error' || response.status === 'error') {
          return;
        }
        const flowMode = response.data?.data?.flowMode;
        this.postPaymentFlow = String(flowMode || '').toLowerCase() === 'post_payment';
      } catch (error) {
        console.error('Failed to read journey flow mode:', error);
      }
    },
    enterSubmittingStage() {
      this.submissionStage = 'submitting';
      this.statusHint = this.postPaymentFlow ? PROPOSAL_SUBMITTING_HINT : VERIFYING_STATUS_HINT;
    },
    enterFetchingStage() {
      if (this.postPaymentFlow) {
        if (this.submissionStage === 'fetching') {
          return;
        }
        this.submissionStage = 'fetching';
        this.statusHint = FETCHING_PAYMENT_HINT;
        return;
      }
      this.statusHint = VERIFYING_STATUS_HINT;
    },
    isProposalResponseSuccess(response) {
      return String(response?.data?.data?.proposal_response?.status?.code) === '200';
    },
    async showPaymentRedirectPause() {
      this.submissionStage = 'redirecting';
      this.statusHint = this.postPaymentFlow ? POST_PAYMENT_REDIRECT_HINT : GATEWAY_REDIRECT_HINT;
      this.stopProgressBar();
      this.progressPercent = 100;
      await this.wait(PAYMENT_REDIRECT_PAUSE_MS);
    },
    wait(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    startProgressBar() {
      this.stopProgressBar();
      const startedAt = Date.now();
      this.pollDeadline = startedAt + PAYMENT_DETAILS_POLL_TIMEOUT_MS;
      this.progressPercent = 0;
      this.remainingMs = PAYMENT_DETAILS_POLL_TIMEOUT_MS;
      this.progressTimer = setInterval(() => {
        const elapsed = Date.now() - startedAt;
        const remaining = Math.max(0, PAYMENT_DETAILS_POLL_TIMEOUT_MS - elapsed);
        this.remainingMs = remaining;
        const pct = Math.min(100, (elapsed / PAYMENT_DETAILS_POLL_TIMEOUT_MS) * 100);
        this.progressPercent = Math.round(pct * 10) / 10;
        if (remaining <= 0) {
          this.stopProgressBar();
        }
      }, 200);
    },
    stopProgressBar() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer);
        this.progressTimer = null;
      }
    },
    restoreSessionFromStorage() {
      try {
        if (sessionStorage.getItem('journey_id')) {
          this.journeyId = JSON.parse(sessionStorage.getItem('journey_id'));
          this.$store.commit('setJourneyId', this.journeyId);
        } else if (this.$route.query.journeyId) {
          const raw = this.$route.query.journeyId;
          this.journeyId = typeof raw === 'string' && raw.startsWith('"')
            ? JSON.parse(raw)
            : raw;
          sessionStorage.setItem('journey_id', JSON.stringify(this.journeyId));
          this.$store.commit('setJourneyId', this.journeyId);
        } else if (this.$store.state.journeyId) {
          this.journeyId = this.$store.state.journeyId;
        }

        this.configData = this.$store.state.configData || null;
        const planRaw =
          sessionStorage.getItem('selectedPlanForCheckout')
          || sessionStorage.getItem('selected_plan_data');
        if (planRaw) {
          this.selectedPlan = JSON.parse(planRaw);
        }
        const premiumRaw = sessionStorage.getItem('selected_plan_data');
        if (premiumRaw) {
          this.selectedPremiumPlan = JSON.parse(premiumRaw);
        }
      } catch (err) {
        console.error('Failed to restore proposal submission session:', err);
      }
    },
    formatApiErrors(errors, fallback = PAYMENT_GATEWAY_FALLBACK_MESSAGE) {
      if (!Array.isArray(errors) || errors.length === 0) {
        return fallback;
      }
      return errors
        .map((element, index) => `${index + 1}. ${stripHtml(element.errorDisplayMessage)}`)
        .join('<br/>');
    },
    fallbackToDiy(message) {
      this.showDiyError(message);
    },
    showDiyError(message) {
      this.pageActive = false;
      this.showError = true;
      this.errorMessage = message || PAYMENT_GATEWAY_FALLBACK_MESSAGE;
      this.statusHint = '';
      this.submissionStage = 'submitting';
      this.stopProgressBar();
      try {
        sessionStorage.setItem(
          DIY_PROPOSAL_ERROR_STORAGE_KEY,
          JSON.stringify({ message: this.errorMessage }),
        );
      } catch {
        /* ignore */
      }
    },
    goToDiy() {
      const query = {};
      if (this.journeyId) {
        query.journeyId = this.journeyId;
      }
      this.$router.push({ path: DIY_CHECK_PATH, query });
    },
    async proposalSubmission() {
      try {
        const reqBody = {
          journey_id: this.journeyId,
          redirect_url: buildPaymentStatusRedirectUrl(),
        };
        const response = await this.backendApi('saveProposal', reqBody, {});
        if (!this.pageActive) {
          return;
        }
        if (
          (response?.data?.errors && response.data.errors.length > 0)
          || !response
          || response === 'error'
          || response.status === 'error'
        ) {
          this.fallbackToDiy(this.formatApiErrors(response?.data?.errors));
          return;
        }
        await this.paymentUrlPoll();
      } catch (error) {
        console.error('An error occurred during proposal submission:', error);
        this.fallbackToDiy(PAYMENT_GATEWAY_FALLBACK_MESSAGE);
      }
    },
    async paymentUrlPoll(override = false) {
      try {
        if (override) {
          this.startProgressBar();
        }
        if (!this.postPaymentFlow) {
          this.statusHint = VERIFYING_STATUS_HINT;
        }
        const endTime = this.pollDeadline || (Date.now() + PAYMENT_DETAILS_POLL_TIMEOUT_MS);
        const reqData = [this.journeyId, PAYMENT_URL_STAGE, override ? 'true' : 'false'];
        const headers = {};

        while (this.pageActive && Date.now() < endTime) {
          const response = await this.backendApi('getProposalWithStage', reqData, headers, 'get');
          if (!this.pageActive) {
            return;
          }
          if (!response || response === 'error' || response.status === 'error' || !response.data) {
            this.fallbackToDiy(PAYMENT_GATEWAY_FALLBACK_MESSAGE);
            return;
          }

          if (response.data?.errors && response.data.errors.length > 0) {
            const handled = await this.handlePaymentUrlErrors(response.data.errors, override);
            if (handled) {
              return;
            }
          }

          if (response.data.errors.length === 0) {
            const proposalResponse = response.data.data?.proposal_response;
            if (proposalResponse !== null && proposalResponse?.errors?.length > 0) {
              const handled = await this.handlePaymentUrlErrors(proposalResponse.errors, override);
              if (handled) {
                return;
              }
            }
            if (this.handleInspectionRequired(response)) {
              return;
            }
            if (this.postPaymentFlow && this.isProposalResponseSuccess(response)) {
              this.enterFetchingStage();
            }
            if (await this.tryHandlePremiumOrPaymentUrl(response)) {
              return;
            }
          }
          await this.wait(PAYMENT_DETAILS_POLL_INTERVAL_MS);
        }

        if (this.pageActive) {
          this.fallbackToDiy(PAYMENT_GATEWAY_FALLBACK_MESSAGE);
        }
      } catch (error) {
        console.error('An error occurred during payment URL polling:', error);
        this.fallbackToDiy(PAYMENT_GATEWAY_FALLBACK_MESSAGE);
      }
    },
    async handlePaymentUrlErrors(errors, override) {
      const paymentInProgressError = errors.find(
        (error) => error.errorCode === 'PAYMENT_IN_PROGRESS',
      );
      if (paymentInProgressError && !override) {
        await this.paymentUrlPoll(true);
        return true;
      }
      this.fallbackToDiy(this.formatApiErrors(errors));
      return true;
    },
    handleInspectionRequired(response) {
      const policyStatus = response.data?.data?.proposal_response?.policy?.policyStatus;
      if (
        response.data?.data?.payment_url
        || !['Scrutiny', 'INSPECTION REQUIRED'].includes(policyStatus)
      ) {
        return false;
      }
      const inspectionLink =
        response?.data?.data?.proposal_response?.inspectionDetails?.selfInspectionLink;
      const inspectionvalidTill =
        response?.data?.data?.proposal_response?.inspectionDetails?.inspectionValidTill;
      if (inspectionLink) {
        sessionStorage.setItem('inspectionLink', JSON.stringify(inspectionLink));
      }
      if (inspectionvalidTill) {
        sessionStorage.setItem('inspectionvalidTill', JSON.stringify(inspectionvalidTill));
      }
      sessionStorage.setItem(
        'payment-url-response',
        JSON.stringify(response?.data?.data?.proposal_response?.policy),
      );
      sessionStorage.setItem(
        'payment-poll-url',
        JSON.stringify(response?.data?.data?.proposal_response?.ManufacturerTracking),
      );
      this.pageActive = false;
      this.stopProgressBar();
      navigateToPaymentStatusOutcome(this.$router, {
        outcome: PAYMENT_STATUS_OUTCOME_INSPECTION,
        journeyId: this.journeyId,
      });
      return true;
    },
    async tryHandlePremiumOrPaymentUrl(response) {
      const receiptAmountRaw = response.data?.data?.proposal_response?.payment?.receiptAmount;
      const selectedPlanData = this.selectedPremiumPlan
        || (() => {
          try {
            return JSON.parse(sessionStorage.getItem('selected_plan_data'));
          } catch {
            return null;
          }
        })();

      if (receiptAmountRaw && !this.ignorePremiumDifference && selectedPlanData?.totalPremiumDetails?.[0]) {
        const receiptAmount = parseInt(receiptAmountRaw, 10);
        const quotedAmount = parseInt(
          selectedPlanData.totalPremiumDetails[0].totalPremiumWithTax,
          10,
        );
        const difference = receiptAmount - quotedAmount;
        if (difference > 5 || difference < -5) {
          this.openPremiumDifference(response, quotedAmount, receiptAmount);
          return true;
        }
      }

      if (response.data?.data?.payment_url != null) {
        await this.processPaymentUrlResponse(response);
        return true;
      }
      return false;
    },
    openPremiumDifference(response, quotedAmount, receiptAmount) {
      this.premiumChoiceHandled = false;
      this.premiumDifferenceResponse = response;
      this.quotedPremium = quotedAmount;
      this.receivedPremium = receiptAmount;
      this.showPremiumDifferenceModal = true;
      this.stopProgressBar();
    },
    handlePremiumDifferenceYes() {
      if (this.premiumChoiceHandled) {
        return;
      }
      this.premiumChoiceHandled = true;
      this.showPremiumDifferenceModal = false;
      this.ignorePremiumDifference = true;
      if (!this.premiumDifferenceResponse) {
        return;
      }
      const response = this.premiumDifferenceResponse;
      if (response.data.data.payment_url != null) {
        this.processPaymentUrlResponse(response);
        return;
      }
      this.premiumDifferenceResponse = null;
      this.pageActive = true;
      this.startProgressBar();
      this.paymentUrlPoll();
    },
    handlePremiumDifferenceNo() {
      if (this.premiumChoiceHandled) {
        return;
      }
      this.premiumChoiceHandled = true;
      this.showPremiumDifferenceModal = false;
      this.premiumDifferenceResponse = null;
      this.quotedPremium = 0;
      this.receivedPremium = 0;
      this.fallbackToDiy();
    },
    manageOvd(mode) {
      const plan = this.planForDisplay;
      if (!plan?.insuranceAndProducts) {
        return false;
      }
      if (shouldSkipGodigitKycOvd(plan)) {
        sessionStorage.setItem('isUploadDocRequired', JSON.stringify(false));
        return false;
      }
      const OVD_KYC = this.configData?.OVD_KYC || this.$store.state.configData?.OVD_KYC;
      const insurerCode = plan.insuranceAndProducts.insuranceCompanyCode;
      let kycResponse = sessionStorage.getItem('kyc_response');
      kycResponse = kycResponse ? JSON.parse(kycResponse) : null;
      const kycStatus = kycResponse?.kycInfo?.Status;
      const isNotApproved = kycStatus !== 'approved';
      const modeMapping = {
        BEFORE_PAYMENT: 'POST_PROPOSAL',
        AFTER_PAYMENT: 'POST_PAYMENT',
      };
      const requiredMode = modeMapping[mode];
      const configValue = OVD_KYC?.[insurerCode];
      return configValue === requiredMode && (!kycResponse || isNotApproved);
    },
    checkKycMode() {
      const plan = this.planForDisplay;
      if (!plan || shouldSkipGodigitKycOvd(plan)) {
        sessionStorage.setItem('isUploadDocRequired', JSON.stringify(false));
        return false;
      }
      const kycMode = this.configData?.KYCMODE || this.$store.state.configData?.KYCMODE;
      let kyc_mode = kycMode?.[plan.insuranceAndProducts?.productCode];
      if (!kyc_mode) {
        kyc_mode = kycMode?.[plan.insuranceAndProducts?.insuranceCompanyCode];
      }
      if (kyc_mode === 'POST') {
        sessionStorage.setItem('nextRef', JSON.stringify('payment'));
        this.$router.push('/ckyc-form?nextRef=payment');
        return true;
      }
      return false;
    },
    async processPaymentUrlResponse(response) {
      const paymentUrl = response.data.data.payment_url;
      const proposalResponse = response.data.data?.proposal_response;
      const plan = this.planForDisplay;
      const insuranceCompanyCode = plan?.insuranceAndProducts?.insuranceCompanyCode;
      const PAY_SETTINGS = this.configData?.PAY_SETTINGS || this.$store.state.configData?.PAY_SETTINGS;

      if (this.manageOvd('BEFORE_PAYMENT')) {
        if (proposalResponse?.ManufacturerTracking?.applicationNumber) {
          sessionStorage.setItem(
            'applicationNumber',
            JSON.stringify(proposalResponse.ManufacturerTracking.applicationNumber),
          );
        }
        if (proposalResponse) {
          sessionStorage.setItem('proposal_response', JSON.stringify(proposalResponse));
        }
        if (paymentUrl) {
          sessionStorage.setItem(PAYMENT_URL_STORAGE_KEY, JSON.stringify(paymentUrl));
        }
        this.pageActive = false;
        this.stopProgressBar();
        if (this.checkKycMode()) {
          return;
        }
        this.$router.push('/upload-ovds?nextRef=payment');
        return;
      }

      if (PAY_SETTINGS && insuranceCompanyCode && PAY_SETTINGS[insuranceCompanyCode] === 'POST') {
        if (String(proposalResponse?.status?.code) !== '200' || !paymentUrl) {
          return;
        }
      }

      await this.showPaymentRedirectPause();
      if (!this.pageActive) {
        return;
      }
      this.pageActive = false;
      this.stopProgressBar();
      sessionStorage.setItem(PAYMENT_URL_STORAGE_KEY, JSON.stringify(paymentUrl));
      this.markGatewayRedirected();
      window.location.href = paymentUrl;
    },
  },
};
</script>

<style scoped>
.payment-status-page,
.wrapper,
.inner-wrapper {
  max-width: 100%;
  overflow-x: hidden;
}

.payment-status-insurer-logo {
  width: 100px;
  height: 50px;
  object-fit: contain;
}

.payment-status-content {
  padding: 6.5rem 1.5rem 3rem;
  max-width: min(560px, 100%);
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  overflow-wrap: anywhere;
}

.payment-status-hourglass {
  display: block;
  margin: 0 auto 1.25rem;
}

.payment-status-title {
  margin-bottom: 0.5rem;
}

.payment-status-subtitle {
  margin-bottom: 1rem;
  color: #6c757d;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.payment-status-title--error,
.payment-status-subtitle--error {
  color: #dc3545;
}

.payment-status-countdown {
  margin-bottom: 1rem;
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #212529;
}

.payment-status-progress {
  height: 10px;
  max-width: 420px;
  background-color: #e9ecef;
  border-radius: 999px;
  overflow: hidden;
}

.payment-status-progress .progress-bar {
  height: 100%;
  background-color: #0d6efd;
  transition: width 0.2s linear;
}

.payment-status-hint {
  font-size: 0.9rem;
  color: #6c757d;
}
</style>
